import { Component, OnInit, ViewChild } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { User } from '../../authentication/service/user';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../authentication/service/authentication.service';
import { ContentSearchCriteria } from './content-search-criteria';
import { ContentService } from '../service/content.service';
import { faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
  ActionEvent,
  ActionState,
  MessageDialogComponent,
} from '../../common/message-dialog/message-dialog.component';

@Component({
  selector: 'app-content-search',
  templateUrl: './content-search.component.html',
})
export class ContentSearchComponent implements OnInit {
  @ViewChild('messageDialog', { static: true })
  messageDialog: MessageDialogComponent;

  currentUser: User;

  criteria = new ContentSearchCriteria();
  response: any;

  editIcon = faPencilAlt;
  deleteIcon = faTrashAlt;

  constructor(
    private router: Router,
    private contentService: ContentService,
    private authenticationService: AuthenticationService
  ) {}

  ngOnInit() {
    this.authenticationService.currentUser$.subscribe((currentUser) => {
      this.currentUser = currentUser;
    });
    this.searchAction();
  }

  onPageChanged(event: any) {
    this.criteria.pageNumber = event.page;
    this.searchAction();
  }

  clearAction() {
    this.criteria = new ContentSearchCriteria();
    this.searchAction();
  }

  searchAction() {
    this.contentService
      .findByCriteria(this.criteria)
      .pipe(
        switchMap((response: any) => this.loadMissingEditableValues(response))
      )
      .subscribe((response) => (this.response = response));
  }

  newAction() {
    this.router.navigate(['content-edit/__new__']);
  }

  editAction(content: any) {
    this.router.navigate(['content-edit/' + content.name]);
  }

  isEditable(content: any) {
    return this.editableValue(content) !== false;
  }

  editableLabel(content: any) {
    const editable = this.editableValue(content);
    if (editable == null) {
      return 'Unknown';
    }
    return editable ? 'Yes' : 'No';
  }

  deleteAction(content: any) {
    const messageText =
      'Are you sure you want to delete this Content record. This action cannot be undone.';
    this.messageDialog.start(messageText, content.contentId);
  }

  onDeleteContentConfirmed(actionEvent: ActionEvent) {
    if (actionEvent.state === ActionState.CONFIRMED) {
      this.contentService.removeContent(actionEvent.payload).subscribe(() => {
        this.searchAction();
      });
    }
  }

  private loadMissingEditableValues(response: any) {
    if (!response?.searchResults?.length) {
      return of(response);
    }

    const contentsMissingEditable = response.searchResults.filter((content: any) => content.editable == null);
    if (contentsMissingEditable.length === 0) {
      return of(response);
    }

    return forkJoin(contentsMissingEditable.map((content: any) => this.contentService.findByName(content.name))).pipe(
      map((contents: any[]) => {
        contents.forEach((content) => {
          const searchResult = response.searchResults.find((result: any) => result.name === content.name);
          if (searchResult) {
            searchResult.editable = content.editable;
          }
        });
        return response;
      })
    );
  }

  private editableValue(content: any): boolean | null {
    if (!content || content.editable == null) {
      return null;
    }

    return content.editable === true || content.editable === 'true' || content.editable === 1;
  }
}
